import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamificationApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './LeaderboardPage.css';

const LeaderboardPage = () => {
  const { preferences } = useAuth();
  const [leaderboards, setLeaderboards] = useState({
    points: [],
    chapters: [],
    family_points: [],
    family_chapters: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (preferences?.competitiveMode) {
      loadLeaderboards();
    }
  }, [preferences]);

  const loadLeaderboards = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all leaderboard categories in parallel
      const [pointsRes, chaptersRes, familyPointsRes, familyChaptersRes] = await Promise.all([
        gamificationApi.getLeaderboard({ limit: 8, category: 'points' }),
        gamificationApi.getLeaderboard({ limit: 8, category: 'chapters' }),
        gamificationApi.getLeaderboard({ limit: 8, category: 'family_points' }),
        gamificationApi.getLeaderboard({ limit: 8, category: 'family_chapters' })
      ]);

      setLeaderboards({
        points: pointsRes.data.leaderboard,
        chapters: chaptersRes.data.leaderboard,
        family_points: familyPointsRes.data.leaderboard,
        family_chapters: familyChaptersRes.data.leaderboard
      });
    } catch (error) {
      console.error('Error loading leaderboards:', error);
      setError('Failed to load leaderboards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  if (!preferences) {
    return (
      <div className="leaderboard-page">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  if (!preferences.competitiveMode) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="leaderboard-page">
        <LoadingSpinner size="large" message="Loading leaderboards..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-error">
          <h2>🏆 Leaderboard</h2>
          <p className="error-message">{error}</p>
          <button onClick={loadLeaderboards} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <h1>🏆 Leaderboard</h1>
        <p>See how you stack up against other readers!</p>
      </div>

      <div className="leaderboard-content">
        <div className="leaderboard-grid">
          <div className="leaderboard-section">
            <div className="section-header">
              <h3>🎯 Top points</h3>
              <Link to="/leaderboard/points" className="view-full-link">
                View full list →
              </Link>
            </div>
            <div className="leaderboard-list">
              {leaderboards.points.length > 0 ? (
                leaderboards.points.map((entry) => (
                  <div key={entry.reader.id} className="leaderboard-item">
                    <div className="rank">{getRankIcon(entry.rank)}</div>
                    <div className="player-info">
                      <div className="player-name">{entry.reader.name}</div>
                      <div className="player-stats">
                        {formatNumber(entry.points)} points
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-leaderboard">No data available</div>
              )}
            </div>
          </div>

          <div className="leaderboard-section">
            <div className="section-header">
              <h3>📖 Top chapters</h3>
              <Link to="/leaderboard/chapters" className="view-full-link">
                View full list →
              </Link>
            </div>
            <div className="leaderboard-list">
              {leaderboards.chapters.length > 0 ? (
                leaderboards.chapters.map((entry) => (
                  <div key={entry.reader.id} className="leaderboard-item">
                    <div className="rank">{getRankIcon(entry.rank)}</div>
                    <div className="player-info">
                      <div className="player-name">{entry.reader.name}</div>
                      <div className="player-stats">
                        {formatNumber(entry.chapters)} chapters
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-leaderboard">No data available</div>
              )}
            </div>
          </div>

          <div className="leaderboard-section">
            <div className="section-header">
              <h3>👨‍👩‍👧‍👦 Family points</h3>
              <Link to="/leaderboard/family_points" className="view-full-link">
                View full list →
              </Link>
            </div>
            <div className="leaderboard-list">
              {leaderboards.family_points.length > 0 ? (
                leaderboards.family_points.map((entry) => (
                  <div key={entry.family?.id || entry.id} className="leaderboard-item">
                    <div className="rank">{getRankIcon(entry.rank)}</div>
                    <div className="player-info">
                      <div className="player-name">{entry.family?.name || 'Unknown family'}</div>
                      <div className="player-stats">
                        {formatNumber(entry.points)} points
                        {entry.family?.memberCount && <span className="member-count">{entry.family.memberCount} members</span>}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-leaderboard">No data available</div>
              )}
            </div>
          </div>

          <div className="leaderboard-section">
            <div className="section-header">
              <h3>📚 Family chapters</h3>
              <Link to="/leaderboard/family_chapters" className="view-full-link">
                View full list →
              </Link>
            </div>
            <div className="leaderboard-list">
              {leaderboards.family_chapters.length > 0 ? (
                leaderboards.family_chapters.map((entry) => (
                  <div key={entry.family?.id || entry.id} className="leaderboard-item">
                    <div className="rank">{getRankIcon(entry.rank)}</div>
                    <div className="player-info">
                      <div className="player-name">{entry.family?.name || 'Unknown family'}</div>
                      <div className="player-stats">
                        {formatNumber(entry.chapters)} chapters
                        {entry.family?.memberCount && <span className="member-count">{entry.family.memberCount} members</span>}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-leaderboard">No data available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
